import React, { useState } from 'react';
import { Copy, Check, Clock, Calendar, Phone, Globe, Search } from 'lucide-react';

const VARIABLE_CATEGORIES = [
  {
    title: 'Caller Information',
    icon: Phone,
    description: 'Information about the current phone call',
    variables: [
      { syntax: '{{caller_number}}', label: "Caller's Phone Number", example: '+12125551234' },
      { syntax: '{{called_number}}', label: 'Called Phone Number (Your Telnyx Number)', example: '+18005559876' },
    ],
  },
  {
    title: 'Current Time',
    icon: Clock,
    description: 'Returns time in 12-hour format (e.g. 02:30 PM). Use any IANA timezone.',
    variables: [
      { syntax: '{{current_time_America/New_York}}', label: 'Eastern Time (US)', example: '02:30 PM' },
      { syntax: '{{current_time_America/Chicago}}', label: 'Central Time (US)', example: '01:30 PM' },
      { syntax: '{{current_time_America/Denver}}', label: 'Mountain Time (US)', example: '12:30 PM' },
      { syntax: '{{current_time_America/Los_Angeles}}', label: 'Pacific Time (US)', example: '11:30 AM' },
      { syntax: '{{current_time_America/Phoenix}}', label: 'Arizona (No DST)', example: '12:30 PM' },
      { syntax: '{{current_time_America/Anchorage}}', label: 'Alaska Time', example: '10:30 AM' },
      { syntax: '{{current_time_Pacific/Honolulu}}', label: 'Hawaii Time', example: '09:30 AM' },
      { syntax: '{{current_time_Europe/London}}', label: 'UK / GMT', example: '07:30 PM' },
      { syntax: '{{current_time_Europe/Paris}}', label: 'Central European', example: '08:30 PM' },
      { syntax: '{{current_time_Europe/Berlin}}', label: 'Germany', example: '08:30 PM' },
      { syntax: '{{current_time_Asia/Dubai}}', label: 'Gulf Standard Time', example: '11:30 PM' },
      { syntax: '{{current_time_Asia/Kolkata}}', label: 'India Standard Time', example: '01:00 AM' },
      { syntax: '{{current_time_Asia/Shanghai}}', label: 'China Standard Time', example: '03:30 AM' },
      { syntax: '{{current_time_Asia/Tokyo}}', label: 'Japan Standard Time', example: '04:30 AM' },
      { syntax: '{{current_time_Australia/Sydney}}', label: 'Australian Eastern', example: '06:30 AM' },
      { syntax: '{{current_time_Pacific/Auckland}}', label: 'New Zealand', example: '08:30 AM' },
      { syntax: '{{current_time_UTC}}', label: 'UTC', example: '07:30 PM' },
    ],
  },
  {
    title: 'Current Date',
    icon: Calendar,
    description: 'Returns the full date (e.g. Friday, March 14, 2026). Use any IANA timezone.',
    variables: [
      { syntax: '{{current_date_America/New_York}}', label: 'Eastern Time (US)', example: 'Friday, March 14, 2026' },
      { syntax: '{{current_date_America/Chicago}}', label: 'Central Time (US)', example: 'Friday, March 14, 2026' },
      { syntax: '{{current_date_America/Los_Angeles}}', label: 'Pacific Time (US)', example: 'Friday, March 14, 2026' },
      { syntax: '{{current_date_Europe/London}}', label: 'UK / GMT', example: 'Friday, March 14, 2026' },
      { syntax: '{{current_date_Asia/Kolkata}}', label: 'India Standard Time', example: 'Saturday, March 15, 2026' },
      { syntax: '{{current_date_Asia/Tokyo}}', label: 'Japan Standard Time', example: 'Saturday, March 15, 2026' },
      { syntax: '{{current_date_UTC}}', label: 'UTC', example: 'Friday, March 14, 2026' },
    ],
  },
  {
    title: 'Current Date & Time',
    icon: Globe,
    description: 'Returns full date and time with timezone (e.g. Friday, March 14, 2026 02:30 PM EST).',
    variables: [
      { syntax: '{{current_datetime_America/New_York}}', label: 'Eastern Time (US)', example: 'Friday, March 14, 2026 02:30 PM EST' },
      { syntax: '{{current_datetime_America/Chicago}}', label: 'Central Time (US)', example: 'Friday, March 14, 2026 01:30 PM CST' },
      { syntax: '{{current_datetime_America/Los_Angeles}}', label: 'Pacific Time (US)', example: 'Friday, March 14, 2026 11:30 AM PST' },
      { syntax: '{{current_datetime_Europe/London}}', label: 'UK / GMT', example: 'Friday, March 14, 2026 07:30 PM GMT' },
      { syntax: '{{current_datetime_Asia/Kolkata}}', label: 'India Standard Time', example: 'Saturday, March 15, 2026 01:00 AM IST' },
      { syntax: '{{current_datetime_Asia/Tokyo}}', label: 'Japan Standard Time', example: 'Saturday, March 15, 2026 04:30 AM JST' },
      { syntax: '{{current_datetime_UTC}}', label: 'UTC', example: 'Friday, March 14, 2026 07:30 PM UTC' },
    ],
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all duration-200 flex-shrink-0"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function DefaultVariablesTab() {
  const [search, setSearch] = useState('');

  const filtered = VARIABLE_CATEGORIES.map((cat) => ({
    ...cat,
    variables: cat.variables.filter(
      (v) =>
        v.syntax.toLowerCase().includes(search.toLowerCase()) ||
        v.label.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => cat.variables.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Default Variables</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Use these variables in your <strong>System Prompt</strong> or <strong>Greeting Message</strong>.
            They are automatically replaced with real values at call time.
          </p>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <p className="text-sm text-foreground font-medium mb-1">How to use</p>
        <p className="text-sm text-muted-foreground">
          Paste any variable into your prompt using the <code className="px-1.5 py-0.5 bg-background rounded text-xs font-mono text-primary">{'{{variable_name}}'}</code> syntax.
          For example:
        </p>
        <div className="mt-2 bg-background rounded-md p-3 font-mono text-xs text-muted-foreground border border-border">
          The current time is <span className="text-primary">{'{{current_time_America/New_York}}'}</span> and you are speaking with caller <span className="text-primary">{'{{caller_number}}'}</span>.
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          You can use <strong>any valid IANA timezone</strong> — the examples below are just common ones.
          Full list: <a href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">IANA Time Zones</a>
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search variables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {filtered.map((category) => {
        const Icon = category.icon;
        return (
          <div key={category.title} className="border border-border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 border-b border-border">
              <Icon className="w-4 h-4 text-primary" />
              <h5 className="text-sm font-semibold text-foreground">{category.title}</h5>
              <span className="text-xs text-muted-foreground ml-auto">{category.description}</span>
            </div>
            <div className="divide-y divide-border">
              {category.variables.map((v) => (
                <div
                  key={v.syntax}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors"
                >
                  <code className="text-xs font-mono text-primary bg-primary/5 px-2 py-1 rounded flex-shrink-0">
                    {v.syntax}
                  </code>
                  <span className="text-sm text-muted-foreground truncate flex-1">{v.label}</span>
                  <span className="text-xs text-muted-foreground/60 font-mono hidden sm:block flex-shrink-0">
                    {v.example}
                  </span>
                  <CopyButton text={v.syntax} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No variables match "<strong>{search}</strong>"
        </div>
      )}
    </div>
  );
}
